import type { HistorySample } from '../api/types';

// F11-1: todayCo2Kg removed (Cycli/CO2 tiles removed in cycle #36, field was dead code).
// F11-1: computeCyclesToday removed (no UI consumer since cycle #36, was exported but unused).
export interface SavingsResult {
  todayEur: number;
  todayPvKwh: number;
  todayLoadKwh: number;
  // Cycle #50 (Bram 2026-05-22): per-bron breakdown afgeleid van history.
  // Gebruikt als fallback wanneer /api/savings/total nog niet beschikbaar is
  // (504/timeout op trage Pi's) zodat CombinedSavingsTile altijd uitsplitsing toont.
  todayPvSelfUseEur: number;
  todayPvExportEur: number;
  todayArbitrageEur: number;
}

const STEP_HOURS = 5 / 60; // 5-min sample = 1/12 h

export function computeSavings(samples: HistorySample[]): SavingsResult {
  let pvKwh = 0;
  let loadKwh = 0;
  let pvSelfUseEur = 0;
  let pvExportEur = 0;
  let arbitrageEur = 0;

  for (const s of samples) {
    const pvKw = (s.pv_w ?? 0) / 1000;
    const loadKw = (s.grid_load_w ?? 0) / 1000;
    const gridKw = (s.grid_w ?? 0) / 1000;       // signed: + = export, - = import
    const buy = s.buy_eur_kwh ?? 0;
    const sell = s.sell_eur_kwh ?? 0;

    const pvSampleKwh = pvKw * STEP_HOURS;
    const loadSampleKwh = loadKw * STEP_HOURS;

    pvKwh += pvSampleKwh;
    loadKwh += loadSampleKwh;

    // PV → load (vermeden import) wordt afgerekend tegen buy-prijs.
    const pvToLoadKwh = Math.min(pvSampleKwh, loadSampleKwh);
    pvSelfUseEur += pvToLoadKwh * buy;

    // PV → grid: surplus boven load, alleen positieve waarde.
    const pvSurplusKwh = Math.max(0, pvSampleKwh - loadSampleKwh);
    pvExportEur += pvSurplusKwh * sell;

    // Arbitrage = NETTO: accu-ontlading naar het net (opbrengst tegen sell)
    // MINUS net-naar-accu laden (kosten tegen buy). Dit spiegelt de
    // serverberekening in api.py ("(c) Arbitrage" in het savings-venster),
    // zodat deze terugval dezelfde grootheid levert als het label belooft.
    //
    // Vóór wachtrij #182 telde deze post alleen de ontlaadkant, met een eigen
    // heuristiek (min(|batteryKw|, gridKw) × sell). Daardoor kon hij per
    // constructie niet negatief worden, terwijl de serverpost op een dag met
    // duur netladen wel degelijk negatief is. De tegel zet client- en
    // serverwaarde in dezelfde rij onder hetzelfde label; juist bij een
    // degraded database valt de tegel expres op deze terugval terug, dus het
    // verschil viel samen met de minste controle.
    //
    // De residu-methode heeft het accuvermogen niet nodig — net als de server
    // leidt ze de accubijdrage af uit meter minus PV-overschot. Dat is hier
    // geen detail: de oude vorm hing aan `battery_w`, en dat register draagt
    // op Vossenjacht alleen pack 1 (CLAUDE.md regel 5), dus daar miste de
    // ontlaadkant ook nog eens de helft van de accu.
    //
    // De last is household + backup, net als server-side
    // (AVG(household_load_w + backup_load_w) in het savings-venster) en in lijn
    // met teller 33179 "Grid Load + Back-Up Load", die de energiebalans sluit
    // — zie `dagverbruikKwh` in derive.ts, wachtrij #201. De PV-termen
    // hierboven rekenen nog met household-only; die afwijking van de server is
    // ouder dan dit item en staat apart in de wachtrij.
    const arbLoadKw = loadKw + (s.backup_w ?? 0) / 1000;
    if (gridKw > 0) {
      const pvToGridKw = Math.max(0, pvKw - arbLoadKw);
      const batteryExportKw = Math.max(0, gridKw - pvToGridKw);
      arbitrageEur += batteryExportKw * STEP_HOURS * sell;
    } else if (gridKw < 0) {
      const loadAfterPvKw = Math.max(0, arbLoadKw - pvKw);
      const gridToBatteryKw = Math.max(0, -gridKw - loadAfterPvKw);
      arbitrageEur -= gridToBatteryKw * STEP_HOURS * buy;
    }
  }

  const todayEur = pvSelfUseEur + pvExportEur + arbitrageEur;

  return {
    todayEur,
    todayPvKwh: pvKwh,
    todayLoadKwh: loadKwh,
    todayPvSelfUseEur: pvSelfUseEur,
    todayPvExportEur: pvExportEur,
    todayArbitrageEur: arbitrageEur,
  };
}
